import type { StructuredContentDocument } from '@ria-client/supergraph/structuredContent/types';
import { isStructuredContent } from '@ria-client/supergraph/structuredContent/utils';

export interface TableOfContentsHeadingProps {
  id: string;
  header: string;
}

export function getAllChildrenStrings(
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string {
  if (typeof sc.props.children === 'string') return prevValue.concat(sc.props.children);

  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') prevValue = prevValue.concat(child);
      if (isStructuredContent(child)) {
        const marketplaceField = child.props.marketplaceField;
        if (
          typeof marketplaceField === 'object' &&
          marketplaceField !== null &&
          'value' in marketplaceField &&
          typeof marketplaceField.value === 'string'
        ) {
          prevValue = prevValue.concat(marketplaceField.value);
        } else {
          prevValue = getAllChildrenStrings(child, prevValue);
        }
      }
    }
  }

  if (isStructuredContent(sc.props.children)) {
    prevValue = getAllChildrenStrings(sc.props.children, prevValue);
  }

  return prevValue;
}

export function getHeadingId(childrenString: string) {
  return childrenString
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();
}

export function getChildrenHeadings(
  parentDocument: StructuredContentDocument,
): TableOfContentsHeadingProps[] {
  const contentHeadings: TableOfContentsHeadingProps[] = [];

  function findContentHeadings(doc: StructuredContentDocument) {
    if (doc.type === 'ContentHeading' && doc.props.level === 2) {
      const header = getAllChildrenStrings(doc);
      contentHeadings.push({ id: getHeadingId(header), header });
    }

    if (Array.isArray(doc.props.children)) {
      doc.props.children.forEach(child => {
        if (isStructuredContent(child)) findContentHeadings(child);
      });
    }
  }

  findContentHeadings(parentDocument);
  return contentHeadings;
}

export const COMPONENT_ID_DESKTOP = 'table-of-contents-desktop';
export const COMPONENT_ID_MOBILE = 'table-of-contents-mobile';
